/**
 * UserSelector Component
 *
 * Multi-select user picker with search functionality.
 * Allows admins to search and select multiple users for notifications and banners.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../lib/api-client';

interface User {
  id: string;
  handle: string;
  display_name: string | null;
  avatar: string | null;
}

interface UserSelectorProps {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  placeholder?: string;
  maxHeight?: string;
}

export function UserSelector({
  selectedUserIds,
  onChange,
  placeholder = 'Search users by handle or display name...',
  maxHeight = 'max-h-60'
}: UserSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch users on mount
  useEffect(() => {
    void fetchUsers();
  }, []);

  // Memoize filtered users calculation for performance
  const filteredUsers = useMemo(() => {
    if (searchQuery.trim() === '') {
      return users.filter(u => !selectedUserIds.includes(u.id));
    }

    const query = searchQuery.toLowerCase();
    return users.filter(
      u =>
        !selectedUserIds.includes(u.id) &&
        (u.handle.toLowerCase().includes(query) || u.display_name?.toLowerCase().includes(query))
    );
  }, [searchQuery, users, selectedUserIds]);

  // Update selected users when selectedUserIds or users changes
  useEffect(() => {
    const selected = users.filter(u => selectedUserIds.includes(u.id));
    setSelectedUsers(selected);
  }, [selectedUserIds, users]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.getUsers({ limit: 1000 });
      setUsers(response.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectUser = (user: User) => {
    const newSelectedIds = [...selectedUserIds, user.id];
    onChange(newSelectedIds);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleRemoveUser = (userId: string) => {
    const newSelectedIds = selectedUserIds.filter(id => id !== userId);
    onChange(newSelectedIds);
  };

  return (
    <div className='space-y-2'>
      {/* Selected Users */}
      {selectedUsers.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {selectedUsers.map(user => (
            <div
              key={user.id}
              className='flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm dark:bg-blue-900'
            >
              {user.avatar && (
                <img src={user.avatar} alt={user.handle} className='h-5 w-5 rounded-full' />
              )}
              <span className='font-medium text-blue-900 dark:text-blue-100'>
                {user.display_name || user.handle}
              </span>
              <button
                type='button'
                onClick={() => handleRemoveUser(user.id)}
                className='text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100'
              >
                <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className='relative'>
        <input
          type='text'
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => {
            // Delay to allow click on dropdown items
            setTimeout(() => setShowDropdown(false), 200);
          }}
          placeholder={placeholder}
          className='w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
        />

        {/* Dropdown */}
        {showDropdown && !isLoading && filteredUsers.length > 0 && (
          <div
            className={`absolute z-10 mt-1 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700 ${maxHeight}`}
          >
            {filteredUsers.slice(0, 50).map(user => (
              <button
                key={user.id}
                type='button'
                onClick={() => handleSelectUser(user)}
                className='flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600'
              >
                {user.avatar && (
                  <img src={user.avatar} alt={user.handle} className='h-8 w-8 rounded-full' />
                )}
                <div className='flex-1'>
                  <div className='font-medium text-gray-900 dark:text-white'>
                    {user.display_name || user.handle}
                  </div>
                  {user.display_name && (
                    <div className='text-sm text-gray-500 dark:text-gray-400'>@{user.handle}</div>
                  )}
                </div>
              </button>
            ))}
            {filteredUsers.length > 50 && (
              <div className='px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400'>
                Showing first 50 results. Refine your search for more.
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className='absolute mt-1 w-full rounded-lg border border-gray-300 bg-white p-4 text-center shadow-lg dark:border-gray-600 dark:bg-gray-700'>
            <div className='text-sm text-gray-500 dark:text-gray-400'>Loading users...</div>
          </div>
        )}

        {/* No Results */}
        {showDropdown && !isLoading && filteredUsers.length === 0 && searchQuery && (
          <div className='absolute mt-1 w-full rounded-lg border border-gray-300 bg-white p-4 text-center shadow-lg dark:border-gray-600 dark:bg-gray-700'>
            <div className='text-sm text-gray-500 dark:text-gray-400'>No users found</div>
          </div>
        )}
      </div>

      {/* Selected Count */}
      {selectedUserIds.length > 0 && (
        <div className='text-sm text-gray-600 dark:text-gray-400'>
          {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
}
